import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { FadeIn } from "@/components/Reveal";

export default function AssessmentPage() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Free Business Risk Readiness Assessment | TSM Group CLE";
    api.get("/api/assessment/questions").then((r) => setQuestions(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/assessment", { answers, email, business_name: businessName, consent });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setBusy(false);
    }
  };

  const bandColor = { "At Risk": "bg-red-50 border-red-200 text-red-800", Developing: "bg-amber-50 border-amber-200 text-amber-800", Prepared: "bg-emerald-50 border-emerald-200 text-emerald-800" }[result?.band || "Developing"];

  return (
    <div className="bg-paper pb-24 pt-14 lg:pt-20" data-testid="assessment-page">
      <div className="container-tsm max-w-3xl">
        <FadeIn>
          <Link to="/mitivise" className="eyebrow" data-testid="assessment-back">MITIVISE</Link>
          <h1 className="mt-6 font-serif text-4xl font-semibold tracking-tight text-navy sm:text-5xl" data-testid="assessment-headline">
            Free Business Risk Readiness Assessment
          </h1>
          <p className="mt-4 text-ink/70">Eight plain-language questions. No account required. You'll get a brief, personalized readiness summary.</p>
        </FadeIn>

        {result ? (
          <FadeIn delay={0.1}>
            <div className={`mt-10 rounded-lg border p-8 ${bandColor}`} data-testid="assessment-result">
              <div className="flex items-center gap-5">
                <span className="font-serif text-6xl font-semibold" data-testid="assessment-score">{result.score}</span>
                <div>
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em]">{result.band}</p>
                  <p className="mt-1 max-w-md text-sm">{result.message}</p>
                </div>
              </div>
              {result.focus_areas.length > 0 && (
                <div className="mt-6 border-t border-black/10 pt-5">
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em]">Focus on these first</p>
                  <ul className="mt-3 space-y-2 text-sm">
                    {result.focus_areas.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <ArrowRight className="mt-0.5 h-3.5 w-3.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-6 text-xs italic opacity-70">{result.disclaimer}</p>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/mitivise" data-testid="assessment-cta-platform" className="btn-gold">Build Your Compliance Systems</Link>
              <Link to="/#request" data-testid="assessment-cta-service" className="btn-outline">Request a Service</Link>
            </div>
          </FadeIn>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="assessment-form">
            {questions.map((q, qi) => (
              <FadeIn key={q.id} delay={qi * 0.05}>
                <fieldset className="rounded-lg border border-navy/10 bg-white p-6" data-testid={`assessment-question-${qi + 1}`}>
                  <legend className="px-1 text-sm font-semibold text-navy">
                    {qi + 1}. {q.q}
                  </legend>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {q.options.map((opt, oi) => (
                      <label
                        key={opt}
                        className={`cursor-pointer rounded-sm border px-4 py-3 text-sm transition-colors ${
                          answers[q.id] === oi ? "border-gold bg-gold/10 font-semibold text-navy" : "border-navy/15 text-ink/70 hover:border-gold/50"
                        }`}
                      >
                        <input type="radio" name={q.id} className="sr-only" checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} data-testid={`assessment-option-${q.id}-${oi}`} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </FadeIn>
            ))}

            <FadeIn delay={0.2}>
              <div className="rounded-lg border border-navy/10 bg-white p-6">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/60">Optional</p>
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-navy/60" htmlFor="assessment-email">Email for your summary</label>
                    <input id="assessment-email" type="email" className="w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-navy focus:border-gold focus:outline-none" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="assessment-email" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-navy/60" htmlFor="assessment-business">Business name</label>
                    <input id="assessment-business" className="w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-navy focus:border-gold focus:outline-none" value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="assessment-business" />
                  </div>
                </div>
                <label className="mt-5 flex items-start gap-3 text-sm text-ink/70">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#C5A059]" data-testid="assessment-consent" />
                  Email me business education, workshop announcements, and MITIVISE offers. (Optional — not pre-checked.)
                </label>
              </div>
            </FadeIn>

            <FadeIn delay={0.25}>
              <button type="submit" disabled={busy || Object.keys(answers).length < questions.length} data-testid="assessment-submit" className="btn-gold w-full disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Show My Readiness Summary
              </button>
              <p className="mt-4 text-center text-xs italic text-ink/50">
                A general business indicator — not a legal or compliance determination.
              </p>
            </FadeIn>
          </form>
        )}
      </div>
    </div>
  );
}
